/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import {packageItemUpsellPersonType, sortPackageItemsByPosition} from "utils/utility";
import BookingPackageUpsellsHeader from "pages/Front/Booking/components/PackageUpsell/BookingPackageUpsellsHeader";
import PackageUpsellItem from "pages/Front/Booking/components/PackageUpsell/PackageUpsellItem";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {useClearUpsells} from "utils/packageUtils";
import {toast} from "react-toastify";
import useSellerTimeslots from "utils/hooks/useSellerTimeslots";
import {ArrowUturnLeftIcon} from "@heroicons/react/24/solid";
import {useGetGroupedUpsells} from "utils/upsellsUtils";
import GroupedPackageUpsellItem from "pages/Front/Booking/components/PackageUpsell/GroupedPackageUpsellItem";
import CombinationOfVariantPackageUpsellItem
    from "pages/Front/Booking/components/PackageUpsell/CombinationOfVariantPackageUpsellItem";

interface BookingTimeslotParams {}

interface BookingUpsellsProps extends RouteComponentProps<BookingTimeslotParams> {
    backHandler: () => void;
    finishHandler: () => void;
    seller_id: number;
}

const SellerUpsells = (props: BookingUpsellsProps) => {
    const packageItemsWithoutGroupOptions = useTypedSelector(state => state.bookingState.bookingProcess?.availablePackageItems?.upsells);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const bookingFilter = bookingProcess.filter;
    const packageCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const persons = +bookingFilter.adults + +bookingFilter.children;
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const clearPackagesHook = useClearUpsells(bookingProcess);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);

    const {bookWithAutomaticTimeslot, isLoading} = useSellerTimeslots(
        () => {},
        props.history,
        [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots],
        props.finishHandler,
        props.seller_id
    );

    const {t} = useTranslation();
    const sortedPackageItemsList = packageItemsWithoutGroupOptions ? sortPackageItemsByPosition(packageItemsWithoutGroupOptions) : [];
    const packageItemsList =
        sortedPackageItemsList.length > 0
            ? sortedPackageItemsList
                  .filter(packageItem => {
                      return packageItemUpsellPersonType(bookingFilter, packageItem.person_type);
                  })
                  .filter(packageItem => {
                      if (packageItem.minimum_persons_count === 0) return true;
                      if (packageItem.minimum_persons_count > 0) {
                          return persons >= packageItem.minimum_persons_count;
                      }
                  })
                  .filter(packageItem => packageItem.is_active != 0)
            : [];

    const submitPackageUpsells = (): void => {
        if (!packageCart || !bookingProcess.selectedTimeslot) {
            toast.error("No packageCart found");
            return;
        }
        bookWithAutomaticTimeslot(null, true);
    };

    const {groupedUpsellsAll} = useGetGroupedUpsells(packageItemsList);

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells();
    }, [selectedUpsells]);

    useEffect(() => {
        console.log("packageItemsList", packageItemsList);
    }, [packageItemsList]);

    return (
        <div className={"SellerUpsells mx-auto w-full"}>
            {packageItemsList.length > 0 && (
                <>
                    <BookingPackageUpsellsHeader className={"!pt-0"} title={t("Please select your extras")} />
                    <div className={"space-y-4"}>
                        {groupedUpsellsAll.map((item, index) =>
                            item.is_variant ? (
                                item?.packageItemVariant?.minimum_count && item.packageItemVariant.minimum_count > 1 ? (
                                    <CombinationOfVariantPackageUpsellItem key={item.packageItemVariant.id} isSeller={true} item={item} />
                                ) : (
                                    <GroupedPackageUpsellItem key={item.variant_id} isSeller={true} item={item} />
                                )
                            ) : (
                                <PackageUpsellItem
                                    key={item.items[0].id}
                                    isSeller={true}
                                    isNewLayout={true}
                                    packageItem={item.items[0]}
                                    selectedPackage={null}
                                />
                            )
                        )}
                    </div>
                </>
            )}
            {packageItemsList.length === 0 && <div className={"text-center"}>{t("No upsells found for selected package.")}</div>}
            <div className={"flex flex-row  mt-6 w-full text-white justify-between"}>
                <button
                    className={
                        "BookingFooterStepsBack font-medium text-base sm:text-lg rounded-md border sm:leading-5 w-auto basis-auto grow-0 bg-gray-600 border-gray-600 hover:bg-gray-500 hover:border-gray-500 px-4 sm:px-3 py-3 flex flex-row items-center justify-center sm:min-w-[210px]"
                    }
                    onClick={props.backHandler}
                >
                    <span className={"flex-none"}>
                        <ArrowUturnLeftIcon className={"w-6 h-6 flex-none sm:mr-2"} />
                    </span>
                    <span className={"text-center w-full hidden sm:inline-flex sm:justify-center"}>{t("Back")}</span>
                </button>
                <ButtonSubmit
                    className={
                        "inline-flex items-center px-4 sm:px-3 py-3 w-auto rounded-md sm:min-w-[210px] !font-medium !text-base sm:!text-lg sm:!leading-none"
                    }
                    icon={"ticket"}
                    id="ButtonSubmitUpsells"
                    isLoading={isLoading}
                    label={selectedUpsells.length === 0 ? t("Continue without upsells") : t("Continue")}
                    onClick={submitPackageUpsells}
                />
            </div>
        </div>
    );
};

export default withRouter(SellerUpsells);
